import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackProductEvent } from "../utils/analytics";
import { isDemoUser } from "@/utils/userFlags";
import { FEATURE_MAP } from "./useAnalytic";
import {useAuth} from "@/features/auth"

export function useDemoTracking() {
  const {user} = useAuth()
  const location = useLocation();
  const isDemo = isDemoUser();
  const demoReturnTrackedRef = useRef(false);

  useEffect(() => {
    if (!user?._id || !user?.isDemo) {
      return;
    }

    const { pathname } = location;
    const firstPagePending = sessionStorage.getItem(
      "__demoSignupFirstPagePending",
    );
    if (firstPagePending === "true") {
      const completedAt =
        sessionStorage.getItem("__demoSignupSignedInAt") ??
        sessionStorage.getItem("__demoSignupCompletedAt");
      const delayMs = completedAt
        ? Math.max(0, Date.now() - parseInt(completedAt, 10))
        : 0;

      trackProductEvent(
        "demo_signup_first_page",
        {
          page: pathname,
          delay_ms: delayMs,
          source: sessionStorage.getItem("__demoSignupSource") ?? "demo_signup",
        },
        user,
      );

      sessionStorage.removeItem("__demoSignupFirstPagePending");
    }

    const feature = FEATURE_MAP[pathname];

    if (feature) {
      const completedAt = sessionStorage.getItem("__demoSignupCompletedAt");
      const alreadyTracked = sessionStorage.getItem("__demoSignupFeatureUsed");

      if (completedAt && !alreadyTracked) {
        trackProductEvent(
          "demo_first_feature_used",
          {
            page: pathname,
            delay_ms: Math.max(0, Date.now() - parseInt(completedAt, 10)),
          },
          user,
        );
        sessionStorage.setItem("__demoSignupFeatureUsed", "true");
      }
    }
  }, [location.pathname, user?._id]);

  useEffect(() => {
    if (!user?._id || !isDemo || demoReturnTrackedRef.current) {
      return;
    }

    const completedAt = sessionStorage.getItem("__demoSignupCompletedAt");

    if (!completedAt) {
      return;
    }

    const hoursSince =
      Math.round(
        ((Date.now() - parseInt(completedAt, 10)) / (1000 * 60 * 60)) * 10,
      ) / 10;

    const returnBucket =
      hoursSince <= 24
        ? "within_24"
        : hoursSince <= 72
          ? "within_72h"
          : "after_72h";

    trackProductEvent(
      "demo_return",
      {
        page: location.pathname,
        hours_since_signup: hoursSince,
        return_bucket: returnBucket,
      },
      user,
    );

    demoReturnTrackedRef.current = true;
  }, [user?._id, isDemo]);
}
